/**
 * Formats an ISO date (or date-time) string as a Belgian `dd/MM/yyyy` date.
 * Plain `yyyy-MM-dd` values are formatted directly (no timezone conversion,
 * since a date-only value has no time component to shift); date-time values
 * are converted to Europe/Brussels local time first.
 * @param value - ISO date or date-time string, or null/undefined.
 * @returns The formatted date, or an empty string when value is absent, or
 * the original value unchanged if it cannot be parsed.
 */
export function formatBelgianDate(value?: string | null): string {
  if (!value) {
    return '';
  }

  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
  }

  const date = parseUtcTimestamp(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('nl-BE', {
    timeZone: 'Europe/Brussels', day: '2-digit', month: '2-digit', year: 'numeric',
  }).format(date);
}

/**
 * Formats an ISO date-time string as a Belgian `dd/MM/yyyy HH:mm` timestamp
 * in Europe/Brussels local time.
 * @param value - ISO date-time string, or null/undefined.
 * @returns The formatted date-time, or an empty string when value is absent,
 * or the original value unchanged if it cannot be parsed.
 */
export function formatBelgianDateTime(value?: string | null): string {
  if (!value) {
    return '';
  }

  const date = parseUtcTimestamp(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('nl-BE', {
    timeZone: 'Europe/Brussels', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(date).replace(',', '');
}

/**
 * Parses an API date-time string into a Date, treating ambiguous
 * timestamps without an explicit timezone as UTC (the API's convention)
 * rather than letting the browser assume local time.
 * @param value - ISO date-time string.
 * @returns The parsed Date (may be Invalid Date if unparsable).
 */
function parseUtcTimestamp(value: string): Date {
  const hasExplicitZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
  const timestamp = value.includes('T') && !hasExplicitZone ? `${value}Z` : value;
  return new Date(timestamp);
}

/**
 * Parses a Belgian `dd/MM/yyyy` date string into an ISO `yyyy-MM-dd` string,
 * validating that the date is real (e.g. rejects 31/02/2024).
 * @param value - Date string in `dd/MM/yyyy` format.
 * @returns The ISO date string, or null if the input is malformed or not a valid calendar date.
 */
export function parseBelgianDate(value: string): string | null {
  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) {
    return null;
  }

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(0);
  date.setUTCHours(0, 0, 0, 0);
  date.setUTCFullYear(year, month - 1, day);

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return `${match[3]}-${match[2]}-${match[1]}`;
}

/**
 * Reformats free-form user input into a partial/complete `dd/MM/yyyy` mask
 * as the user types, by stripping non-digits and re-inserting slashes.
 * @param value - Raw input value from a date text field.
 * @returns The masked value, truncated to at most 8 digits (ddMMyyyy).
 */
export function normalizeBelgianDateInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) {
    return digits;
  }
  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}
